import {
    type FC,
    type HTMLAttributes,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";

interface Props extends HTMLAttributes<HTMLDivElement> {
    error?: ReactNode;
    children: ReactNode;
    onClear?: () => void;
    disableProse?: boolean;
}

const BoxForm: FC<Props> = ({ error, children, onClear, disableProse, ...props }) => {
    const errorRef = useRef<null | HTMLDivElement>(null);

    const [ showError, setShowError ] = useState(false);
    const [ errorHeight, setErrorHeight ] = useState(0);

    useEffect(() => {
        setShowError(!!error);
    }, [ error ]);

    useEffect(() => {
        if(showError && errorRef.current) {
            setErrorHeight(errorRef.current?.scrollHeight);
        } else {
            setErrorHeight(0);
        }
    }, [ showError ]);

    const onClick = () => {
        if(typeof onClear === "function") {
            onClear();
        }

        setShowError(false);
    };

    const className = `${disableProse ? "" : "prose"} ${props.className ?? ""} mx-auto relative p-8`;

    return (
        <div
            {...props}
            className={className}
            style={showError ? {
                marginTop: `${errorHeight + 50}px`,
            } : {}}
        >
            {showError && (
                <div
                    ref={errorRef}
                    className="absolute bottom-full w-[96vw] -mb-3 ml-[calc(50%-48vw-2rem)]"
                >
                    <div
                        className={
                            "flex w-fit bg-[var(--color-surface-raised)] mx-auto rounded-lg shadow-xl z-10 " +
                            "overflow-hidden"
                        }
                    >
                        <div
                            className={
                                "flex justify-center items-center bg-[var(--pill-red-bg)] " +
                                "text-[var(--pill-red-fg)] p-2"
                            }
                        >
                            <FontAwesomeLight icon="exclamation-triangle" />
                        </div>
                        <div className="grow py-2 px-4 text-left min-w-62.5 text-[var(--color-text)]">
                            {error}
                        </div>
                        <button
                            type="button"
                            onClick={onClick}
                            className={
                                "ignore-default p-1 my-2 mr-1 rounded-lg border-none text-[var(--color-text)] " +
                                "cursor-pointer bg-transparent hover:bg-[var(--color-surface-muted)]"
                            }
                        >
                            <FontAwesomeLight icon="times" />
                        </button>
                    </div>
                </div>
            )}
            <div
                className={
                    "relative rounded-lg shadow-xl bg-[var(--color-surface-raised)] p-8 z-10 " +
                    "text-[var(--color-text)]"
                }
            >
                {children}
            </div>
        </div>
    );
};

export default BoxForm;
